import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AuthService, TimezoneAwareDatePipe } from '@twaice-fe/frontend/shared/services';
import { ChatMessage } from '@twaice-fe/shared/models';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { BehaviorSubject, combineLatest, filter, map, Observable, take } from 'rxjs';
import { SvgIconModule } from '../../../../svg-icon/svg-icon.module';
import { alertIconConfig } from '../../../component-sidebar.config';
import { ComponentSidebarService } from '../../../component-sidebar.service';
import { RuleActivity, RuleEvaluation } from '../../../component-sidebar.types';
import { ComponentRuleBreachDetailsService } from '../component-rule-breach-details.service';
import { FilterOptions, TimeLineItem, Transition, UiRuleEvaluation } from './component-rule-transitions.types';
import { isShownUnderFilter, stateMap } from './component-rule-transitions.utils';

@Component({
  selector: 'twaice-fe-alerts-component-rule-transitions',
  imports: [
    CommonModule,
    NzTagModule,
    NzIconModule,
    SvgIconModule,
    NzTimelineModule,
    FormsModule,
    NzCommentModule,
    NzFormModule,
    NzButtonModule,
    NzInputModule,
    NzSelectModule,
    TimezoneAwareDatePipe,
  ],
  providers: [TimezoneAwareDatePipe],
  templateUrl: './component-rule-transitions.component.html',
  styleUrls: ['./component-rule-transitions.component.scss'],
})
export class ComponentRuleTransitionsComponent implements OnInit {
  @Input() ruleBk: string;
  timelineItems$: Observable<{ day: Date; items: TimeLineItem[] }[]>;
  visibleItemCount$ = new BehaviorSubject<number>(5);
  selectedFilter$ = new BehaviorSubject<FilterOptions>(FilterOptions.ALL);
  isLoading$: Observable<boolean>;

  insertCommentTimestamp: Date | null = null;
  storageTimezone: string | undefined = undefined;

  canShowMoreItems = true;

  defaultFilter = FilterOptions.ALL;
  FilterOptions = FilterOptions;
  readonly additionalActivityLabel: string | null;
  newMessage = '';
  submittingMessage = false;

  protected readonly alertIconConfig = alertIconConfig;

  constructor(
    public authService: AuthService,
    private destroy$: DestroyRef,
    private componentRuleBreachDetailsService: ComponentRuleBreachDetailsService,
    private sidebarService: ComponentSidebarService,
    private datePipe: TimezoneAwareDatePipe
  ) {
    this.additionalActivityLabel = this.componentRuleBreachDetailsService.additionalActivityLabel;
    this.componentRuleBreachDetailsService.fetchChatMessages();
  }

  ngOnInit() {
    this.timelineItems$ = combineLatest([
      this.componentRuleBreachDetailsService.getRuleEvaluations$(this.ruleBk),
      this.componentRuleBreachDetailsService.getChatMessages$(),
      this.componentRuleBreachDetailsService.getAdditionalActivity$(),
      this.sidebarService.getSelectedComponent$(),
      this.visibleItemCount$.asObservable(),
      this.selectedFilter$.asObservable(),
      this.componentRuleBreachDetailsService.getStorageTimezone$(),
    ]).pipe(
      takeUntilDestroyed(this.destroy$),
      filter(
        (result): result is [RuleEvaluation[], ChatMessage[], RuleActivity[], string, number, FilterOptions, string | null] =>
          !!result[0] && !!result[1]
      ),
      map(
        ([ruleEvaluations, chatMessages, additionalActivity, componentBk, visibleItemCount, selectedFilter, storageTimezone]) => {
          this.storageTimezone = storageTimezone ?? undefined;
          return this.toGroupedTimeLineItems(
            ruleEvaluations,
            chatMessages,
            additionalActivity,
            componentBk,
            visibleItemCount,
            selectedFilter
          );
        }
      )
    );

    this.isLoading$ = this.componentRuleBreachDetailsService.getRuleEvaluationsLoadingState$(this.ruleBk);
  }

  toGroupedTimeLineItems(
    ruleEvaluations: RuleEvaluation[],
    chatMessages: ChatMessage[],
    additionalActivity: RuleActivity[],
    componentBk: string,
    nTransitions: number = 5,
    selectedFilter: FilterOptions = FilterOptions.ALL
  ): { day: Date; items: TimeLineItem[] }[] {
    const groupedTransitions = this.toGroupedTransitions(ruleEvaluations);
    const groupedChatMessages = this.toGroupedChatMessages(chatMessages, componentBk, ruleEvaluations);
    const additionalItems = additionalActivity.map((activity) => ({ activity, date: activity.date, icon: activity.icon }));

    const items = [
      ...(isShownUnderFilter(selectedFilter, FilterOptions.TRANSITIONS) ? groupedTransitions : []),
      ...(isShownUnderFilter(selectedFilter, FilterOptions.CHAT) ? groupedChatMessages : []),
      ...(isShownUnderFilter(selectedFilter, FilterOptions.ADDITIONAL) ? additionalItems : []),
    ].sort((a, b) => b.date.getTime() - a.date.getTime());

    this.canShowMoreItems = items.length > nTransitions;

    const groupedByDay = items
      .slice(0, nTransitions)
      .reduce<Record<string, { day: Date; items: TimeLineItem[] }>>((acc, item) => {
        const day = this.datePipe.transform(item.date, 'yyyy-MM-dd', this.storageTimezone) || '';
        if (!acc[day]) {
          acc[day] = { day: item.date, items: [] };
        }
        acc[day].items.push(item);
        return acc;
      }, {});

    return Object.values(groupedByDay);
  }

  toUiRuleEvaluation(ruleEvaluation: RuleEvaluation): UiRuleEvaluation {
    return {
      ...stateMap[ruleEvaluation.state],
      ...ruleEvaluation,
    };
  }

  showMoreItems() {
    this.visibleItemCount$.next(this.visibleItemCount$.value + 5);
  }

  changeFilter(activeFilter: FilterOptions) {
    this.selectedFilter$.next(activeFilter);
  }

  postMessage() {
    this.sidebarService
      .getSelectedComponent$()
      .pipe(take(1))
      .subscribe((componentBk) => {
        if (this.newMessage.trim()) {
          this.componentRuleBreachDetailsService.postChatMessage({
            message: this.newMessage,
            tags: ['Smart Alerts', componentBk],
            includeLink: true,
            relatedEntities: [componentBk, this.ruleBk],
            timestamp: this.insertCommentTimestamp
              ? new Date(this.insertCommentTimestamp.getTime() + Math.floor(Math.random() * 1000))
              : undefined,
          });
          this.newMessage = '';
          this.insertCommentTimestamp = null;
        }
      });
  }

  insertComment(date: Date | null) {
    this.insertCommentTimestamp = date;
  }

  private toGroupedTransitions(ruleEvaluations: RuleEvaluation[]): TimeLineItem[] {
    return (
      ruleEvaluations?.reduceRight<Transition[]>((acc, newer, i, arr) => {
        if (i === 0) return acc;

        const older = arr[i - 1];

        if (newer.state !== older.state) {
          acc.push({
            from: this.toUiRuleEvaluation(older),
            to: this.toUiRuleEvaluation(newer),
            date: newer.start,
          });
        }

        return acc;
      }, []) ?? []
    ).map((t) => ({ transition: t, date: t.date, icon: 'info-circle' }));
  }

  private toGroupedChatMessages(
    chatMessages: ChatMessage[],
    componentBk: string,
    ruleEvaluations: RuleEvaluation[]
  ): TimeLineItem[] {
    return chatMessages
      .filter((m) => m.relatedEntities?.includes(componentBk) && m.relatedEntities?.includes(this.ruleBk))
      .filter((m) => new Date(m.timestamp).getTime() > Math.min(...ruleEvaluations.map((e) => e.end.getTime())))
      .map((m) => ({ message: m, date: new Date(m.timestamp), icon: 'message' }));
  }
}
